'use strict';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import {createEventHandler, mapPropsStream} from 'recompose';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/takeUntil';
import {sortByProperty} from '../../../../../core-utilities/filters/filterUtils';
import {I18nComponent} from '../../../../../core-utilities/react-i18n';
import {deepCompareItem, deepCompareLists} from '../../../../utils';
import {ListFieldEditView} from './ListFieldEditView';


export const StreamedComponent = (props$) => {
  const {
    handler: onSingleChange,
    stream: singleChange$,
  } = createEventHandler();

  const {
    handler: onMultipleChange,
    stream: multipleChange$,
  } = createEventHandler();

  singleChange$
    .withLatestFrom(props$)
    .takeUntil(props$.last())
    .subscribe(([ev, props]) => {
      if (props.isSaving)
        return;

      props.onChange([props.field.id, {
        target: {
          value: {
            ...props.value,
            ...ev.value,
          },
        },
      }]);
    });

  multipleChange$
    .withLatestFrom(props$)
    .takeUntil(props$.last())
    .subscribe(([result, props]) => {
      if (props.isSaving)
        return;

      const nextResult = result.map(item => JSON.parse(item));
      nextResult.sort(sortByProperty('id', true));

      props.onChange([props.field.id, {
        target: {
          value: nextResult,
        },
      }]);
    });

  // if single, convert to map
  // if multi, stringify items to work with CheckboxGroup
  const options$ = props$
    .distinctUntilChanged(isOptionsPropsEqual)
    .map(props => {
      const field = props.field;
      if (!isArray(get(field, 'options.items')))
        return [];

      if (get(field, 'options.selectMany')) {
        field.options.next = fixNextOption(field.options);
        return field.options.items.map(item => ({
          label: item.label,
          value: JSON.stringify(item),
        }));
      } else {
        return [
          {
            label: props.emptyPlaceholder,
            value: null,
          },
          ...props.field.options.items.map(item => ({
            label: item.label,
            value: item,
          })),
        ];
      }
    });

  // if single, convert to Dropdown option
  // if multi, stringify results to work with CheckboxGroup
  const value$ = props$
    .distinctUntilChanged(isValuePropsEqual)
    .map(props => {
      const field = props.field;
      const result = props.value;

      if (get(field, 'options.selectMany')) {
        field.options.next = fixNextOption(field.options);
        if (result === undefined) {
          return [].map(item => JSON.stringify(item));
        }
        return result.map(item => JSON.stringify(item));
      } else if (isObject(result)) {
        return {
          label: result.label,
          value: result,
        };
      } else {
        return {
          label: props.emptyPlaceholder,
          value: null,
        };
      }
    });

  return props$
    .combineLatest(
      options$,
      value$,
      (props, options, value) => ({
        ...props,
        options,
        value,
        onSingleChange,
        onMultipleChange,
      }));
};

export const BaseListFieldEditComponent = mapPropsStream(StreamedComponent)(ListFieldEditView);

export const ListFieldEditComponent = I18nComponent(BaseListFieldEditComponent, [
  {code: 'forms.response.default.placeholder', prop: 'emptyPlaceholder'},
  {code: 'forms.list.select.one.placeholder', prop: 'selectOnePlaceholder'},
]);

function isOptionsPropsEqual(prevProps, nextProps) {
  const a = prevProps.field;
  const b = nextProps.field;

  if (a === b || (!a && !b)) {
    return true;
  } else if (!a || !b) {
    return false;
  } else {
    return get(a, 'options.items') === get(b, 'options.items')
      && get(a, 'options.selectMany') === get(b, 'options.selectMany')
      && prevProps.emptyPlaceholder === nextProps.emptyPlaceholder;
  }
}

function isValuePropsEqual(prevProps, nextProps) {
  const a = prevProps.value;
  const b = nextProps.value;

  if (prevProps.emptyPlaceholder !== nextProps.emptyPlaceholder) {
    return false;
  } else if (!isOptionsPropsEqual(prevProps, nextProps)) {
    return false;
  } else if (get(nextProps.field, 'options.selectMany')) {
    return deepCompareLists(a, b);
  } else {
    return deepCompareItem(a, b);
  }
}

function fixNextOption(options) {
  if (options.next <= options.items[options.items.length - 1]) {
    options.next = options.items[options.items.length - 1].id + 1;
  } if (options.next <= options.items.length) {
    options.next = options.items.length + 1;
  }
  return options.next;
}



// WEBPACK FOOTER //
// ./src/js/form-builder/components/response/fields/ListFieldEdit/ListFieldEditComponent.js